import { type ErrorComponentProps, useRouter } from '@tanstack/react-router'
import { CloudOffIcon } from 'lucide-react'

import { Button } from '@kir/ui/components/button'

/**
 * What a screen shows when its loader or component throws: the API mid-deploy, a lost
 * connection, a build the page has outlived. The router's built-in boundary offers no way out,
 * and an installed app has no address bar to reload from, so on a phone the only exit was to
 * close the app. "Try again" runs the loaders again in place, which is what a passing outage
 * needs; "Reload" starts the app over, which is what a stale build needs.
 */
export function RouteError({ reset }: ErrorComponentProps) {
  const router = useRouter()
  return (
    <main className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 bg-background px-6 py-16 text-center text-foreground">
      <CloudOffIcon className="size-8 text-muted-foreground" aria-hidden />
      <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        This screen could not load. Check your connection and try again.
      </p>
      <div className="flex w-full max-w-xs flex-col gap-2 sm:w-auto sm:flex-row">
        <Button
          className="h-11"
          onClick={() => {
            reset()
            void router.invalidate()
          }}
        >
          Try again
        </Button>
        <Button variant="outline" className="h-11" onClick={() => window.location.reload()}>
          Reload the app
        </Button>
      </div>
    </main>
  )
}
